"use client";

import { useState } from "react";
import {
  Briefcase,
  ShieldCheck,
  Zap,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Clock,
  Award,
} from "lucide-react";
import {
  EXECUTIVE_PRACTICES,
  calculatePlacementFee,
} from "@/lib/demos/hiringData";

interface HiringHeroProps {
  currency: "INR" | "USD";
  onRequestSearch: (params: {
    practiceId: string;
    targetCtc: number;
    placementFee: number;
  }) => void;
}

export function HiringHero({ currency, onRequestSearch }: HiringHeroProps) {
  const [selectedPracticeId, setSelectedPracticeId] = useState<string>("c-suite");

  const selectedPractice =
    EXECUTIVE_PRACTICES.find((p) => p.id === selectedPracticeId) ||
    EXECUTIVE_PRACTICES[0];

  const bounds =
    currency === "USD"
      ? selectedPractice.rolesBenchmarkUsd
      : selectedPractice.rolesBenchmarkInr;

  const [annualCtc, setAnnualCtc] = useState<number>(bounds.default);

  // Recalculate when currency or practice changes
  const feeCalculation = calculatePlacementFee(
    selectedPracticeId,
    annualCtc,
    currency
  );

  const sym = currency === "USD" ? "$" : "₹";

  const handlePracticeChange = (id: string) => {
    setSelectedPracticeId(id);
    const p = EXECUTIVE_PRACTICES.find((x) => x.id === id) || EXECUTIVE_PRACTICES[0];
    const newBounds = currency === "USD" ? p.rolesBenchmarkUsd : p.rolesBenchmarkInr;
    setAnnualCtc(newBounds.default);
  };

  const formatCtcDisplay = (amount: number) => {
    if (currency === "USD") {
      return `$${(amount / 1000).toFixed(0)}k`;
    }
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    }
    return `₹${(amount / 100000).toFixed(1)} Lakhs`;
  };

  return (
    <section className="relative pt-12 pb-20 overflow-hidden bg-gradient-to-b from-[#0d0914] via-[#140c21] to-[#0d0914] text-white">
      {/* Background Ambient Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-purple-600/10 blur-[130px] rounded-full pointer-events-none" />
      <div className="absolute top-10 right-10 w-[350px] h-[350px] bg-indigo-600/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Value Prop */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
              <span>Retained Search • Top 1.5% Leadership Talent Pool</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.15]">
              Strategic{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-fuchsia-300 to-indigo-400">
                Executive Search
              </span>{" "}
              &amp; Leadership Placement.
            </h1>

            <p className="text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              We partner with visionary enterprise boards and high-growth unicorns to recruit exceptional Chief Executive, Technology, and Revenue leaders. Every placement is backed by our rigorous competency audits and comprehensive 90 to 180-day retention guarantees.
            </p>

            {/* Credibility Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
              <div className="p-3 rounded-xl bg-[#181126]/80 border border-purple-900/40 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400">Guaranteed</div>
                  <div className="text-xs font-bold text-white">90-180 Day Warranty</div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-[#181126]/80 border border-purple-900/40 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400">Velocity</div>
                  <div className="text-xs font-bold text-white">14-Day First Shortlist</div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-[#181126]/80 border border-purple-900/40 col-span-2 sm:col-span-1 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-fuchsia-500/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-fuchsia-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400">Success Ratio</div>
                  <div className="text-xs font-bold text-white">98.4% Retention Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Placement Fee Calculator */}
          <div className="lg:col-span-5">
            <div className="p-6 sm:p-7 rounded-2xl bg-[#160f24]/90 border border-purple-800/40 shadow-2xl shadow-purple-950/60 backdrop-blur-xl relative">
              <div className="flex items-center justify-between pb-4 border-b border-purple-900/40">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-purple-400" />
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-300">
                    Mandate Fee Estimator
                  </span>
                </div>
                <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
                  Contingency / Retained
                </span>
              </div>

              {/* Practice Track Selector */}
              <div className="mt-5 space-y-2">
                <label className="text-xs font-medium text-slate-300">
                  Select Search Practice:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {EXECUTIVE_PRACTICES.map((practice) => (
                    <button
                      key={practice.id}
                      type="button"
                      onClick={() => handlePracticeChange(practice.id)}
                      className={`p-2.5 rounded-xl text-left border transition-all ${
                        selectedPracticeId === practice.id
                          ? "bg-purple-950/80 border-purple-500 text-white shadow-lg shadow-purple-500/10"
                          : "bg-[#110b1a] border-purple-950/60 text-slate-400 hover:border-purple-800 hover:text-white"
                      }`}
                    >
                      <div className="text-xs font-bold truncate">{practice.title}</div>
                      <div className="text-[10px] text-purple-400 font-medium">
                        {practice.baseFeePercentage}% Placement Fee
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* CTC Slider */}
              <div className="mt-5 space-y-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-300 font-medium">Target Annual CTC Package:</span>
                  <span className="text-sm font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-indigo-300">
                    {formatCtcDisplay(annualCtc)}
                  </span>
                </div>
                <input
                  type="range"
                  min={bounds.min}
                  max={bounds.max}
                  step={currency === "USD" ? 10000 : 500000}
                  value={annualCtc}
                  onChange={(e) => setAnnualCtc(Number(e.target.value))}
                  className="w-full h-2 bg-purple-950 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
                <div className="flex justify-between text-[10px] text-slate-500 font-mono">
                  <span>{formatCtcDisplay(bounds.min)}</span>
                  <span>Benchmark Range</span>
                  <span>{formatCtcDisplay(bounds.max)}</span>
                </div>
              </div>

              {/* Live Cost Breakdown */}
              <div className="mt-6 p-4 rounded-xl bg-[#0f0917] border border-purple-900/40 space-y-2.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Professional Search Fee ({feeCalculation.feePercentage}%):</span>
                  <span className="font-semibold text-white">
                    {sym}{feeCalculation.placementFee.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Applicable GST / Tax (18%):</span>
                  <span className="font-medium text-slate-300">
                    {sym}{feeCalculation.taxAmount.toLocaleString()}
                  </span>
                </div>
                <div className="h-px bg-purple-900/40 my-1" />
                <div className="flex justify-between items-baseline">
                  <div>
                    <div className="text-xs font-bold text-purple-300">Estimated Total Placement:</div>
                    <div className="text-[10px] text-slate-500">Net 15 days on joining</div>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">
                      {sym}{feeCalculation.totalInvoiceAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Guarantees Note */}
              <div className="mt-4 flex items-center justify-between text-[11px] text-slate-400 px-1">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  {selectedPractice.guaranteeDays}-Day Replacement
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5 text-amber-400" />
                  Avg {selectedPractice.averagePlacementDays} Days to Closure
                </span>
              </div>

              {/* Action Button */}
              <button
                type="button"
                onClick={() =>
                  onRequestSearch({
                    practiceId: selectedPracticeId,
                    targetCtc: annualCtc,
                    placementFee: feeCalculation.placementFee,
                  })
                }
                className="mt-5 w-full py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
              >
                <span>Initiate Executive Search</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
